/**
 * Guardian authentication and navigation state.
 *
 * Owns sign-in/session persistence, auth modal vs full-page flows, password-reset
 * deep links, OAuth return handling, and URL-backed page navigation.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  handleGuardianOAuthReturn,
  getGuardianResetTokenFromUrl,
  clearGuardianResetTokenFromUrl,
} from '../components/GuardianAuth';
import { getFrontierModel } from '../data/frontierModels';
import { fetchGuardianMe } from '../api/guardianApi';
import { safeStorage } from '../utils/safeStorage';
import { readJoinTokenFromUrl, getPendingJoinToken, clearPendingJoinToken } from '../utils/departmentJoin';
import { clearGuardianHistoryCache } from '../utils/guardianHistoryCache';
import {
  GUARDIAN_PATHS,
  authModeFromPath,
  viewFromPath,
  pathFromView,
  pathFromAuthMode,
} from '../routes/guardianPaths';

/**
 * Central auth + routing hook for the Guardian chatbot shell.
 *
 * @param {string} scopeProfile - `guardian` requires login; other profiles may use env token.
 * @returns Auth session, UI state, and handlers consumed by App / AppHeader.
 */
export function useGuardianAuth(scopeProfile) {
  const navigate = useNavigate();
  const location = useLocation();

  const [authError, setAuthError] = useState(null);

  const [authToken, setAuthToken] = useState(() => {
    const stored = safeStorage.get('guardianAuthToken');
    if (scopeProfile === 'guardian') {
      return stored;
    }
    // Local-dev token lives in localStorage ('devAuthToken'), never in a
    // REACT_APP_* var: CRA inlines every REACT_APP_* var set at build time
    // into the bundle whatever the code reads, so a NODE_ENV gate could not
    // keep it out (2026-09-25 gap verification; scripts/assert-bundle-clean.js).
    const devToken = process.env.NODE_ENV === 'development'
      ? safeStorage.get('devAuthToken')
      : null;
    return stored || devToken || null;
  });

  const [guardianUser, setGuardianUser] = useState(() => {
    try {
      return JSON.parse(safeStorage.get('guardianUser') || 'null');
    } catch (err) {
      console.warn('Ignoring unreadable saved Guardian user:', err);
      return null;
    }
  });

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [passwordResetToken, setPasswordResetToken] = useState(null);
  const [promoBadgeVisible, setPromoBadgeVisible] = useState(() => {
    return safeStorage.get('guardianAuthPromoDismissed') !== 'true';
  });
  const [entitlements, setEntitlements] = useState(null);
  const [profileEpoch, setProfileEpoch] = useState(0);
  // Terms consent from /me (audit P-4); null until known.
  const [consent, setConsent] = useState(null);

  const authPageMode = useMemo(
    () => authModeFromPath(location.pathname),
    [location.pathname],
  );
  const activeView = useMemo(
    () => viewFromPath(location.pathname),
    [location.pathname],
  );

  const userId = guardianUser?.id || null;
  const requiresGuardianAuth = scopeProfile === 'guardian' && !authToken;

  const setActiveView = useCallback((view) => {
    navigate(pathFromView(view));
  }, [navigate]);

  const setAuthPageMode = useCallback((mode) => {
    if (!mode) {
      navigate(GUARDIAN_PATHS.chat);
      return;
    }
    navigate(pathFromAuthMode(mode));
  }, [navigate]);

  const handleGuardianAuthenticated = useCallback((token, user, nextEntitlements = null) => {
    setAuthToken(token);
    setGuardianUser(user);
    if (nextEntitlements) {
      setEntitlements(nextEntitlements);
    }
    setAuthModalOpen(false);
    setPasswordResetToken(null);
    // An invite link opened before signing in resumes after sign-in.
    navigate(getPendingJoinToken() ? GUARDIAN_PATHS.join : GUARDIAN_PATHS.chat);
  }, [navigate]);

  const applyProfileFromMe = useCallback((user, nextEntitlements) => {
    if (user) {
      // A caller passing /me's `user` alone keeps the known department block.
      setGuardianUser((prev) => {
        const merged = {
          ...user,
          department: 'department' in user ? user.department : (prev?.department ?? null),
        };
        safeStorage.set('guardianUser', JSON.stringify(merged));
        return merged;
      });
    }
    if (nextEntitlements) {
      setEntitlements(nextEntitlements);
    }
  }, []);

  /**
   * Signs the user out and reopens sign-in after a 401 from any Guardian-scoped
   * request (the `/me` check below, or a voice call in useChatApp). Centralizing
   * this avoids duplicating the sign-out sequence at each 401 call site.
   * @param {string} [message] - Shown in the auth banner/modal.
   */
  const handleUnauthorized = useCallback((message = 'Your session expired. Please sign in again.') => {
    safeStorage.remove('guardianAuthToken');
    safeStorage.remove('guardianUser');
    setAuthToken(null);
    setGuardianUser(null);
    setEntitlements(null);
    setAuthError(message);
    setAuthModalOpen(true);
  }, []);

  useEffect(() => {
    if (!authToken || scopeProfile !== 'guardian') return undefined;
    let cancelled = false;
    (async () => {
      const res = await fetchGuardianMe(authToken);
      if (cancelled) return;
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!res.success) return;
      if (res.data?.user) {
        // Department membership (docs/guardian/03 §1.3) rides on the user.
        const user = { ...res.data.user, department: res.data.department ?? null };
        setGuardianUser(user);
        safeStorage.set('guardianUser', JSON.stringify(user));
      }
      if (res.data?.entitlements) {
        setEntitlements(res.data.entitlements);
      }
      setConsent(res.data?.consent ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, scopeProfile, handleUnauthorized, profileEpoch]);

  /** Re-reads /me (after joining or leaving a department). */
  const refreshProfile = useCallback(() => setProfileEpoch((n) => n + 1), []);

  const openAuthModal = useCallback(() => {
    setAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalOpen(false);
  }, []);

  const openAuthPage = useCallback((mode, resetToken = null) => {
    setAuthModalOpen(false);
    setPasswordResetToken(resetToken);
    const path = pathFromAuthMode(mode);
    if (mode === 'reset' && resetToken) {
      navigate(`${path}?resetToken=${encodeURIComponent(resetToken)}`);
      return;
    }
    navigate(path);
  }, [navigate]);

  const closeAuthPage = useCallback(() => {
    setPasswordResetToken(null);
    clearGuardianResetTokenFromUrl();
    navigate(GUARDIAN_PATHS.chat);
  }, [navigate]);

  // Invite links: /join?token=… The token moves to sessionStorage and leaves
  // the address bar/history at once (docs/guardian/03 §1.3).
  useEffect(() => {
    if (location.pathname === GUARDIAN_PATHS.join) readJoinTokenFromUrl();
  }, [location.pathname]);

  // Email reset links: /reset?resetToken=… or legacy /?resetToken=…
  useEffect(() => {
    const token = getGuardianResetTokenFromUrl();
    if (!token) return;
    setPasswordResetToken(token);
    clearGuardianResetTokenFromUrl();
    if (location.pathname !== GUARDIAN_PATHS.reset) {
      navigate(GUARDIAN_PATHS.reset, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await handleGuardianOAuthReturn((token, user) => {
          if (!cancelled) {
            handleGuardianAuthenticated(token, user);
          }
        });
      } catch (err) {
        if (!cancelled) {
          setAuthError(err.message || 'OAuth sign-in failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handleGuardianAuthenticated]);

  const dismissPromoBadge = () => {
    setPromoBadgeVisible(false);
    safeStorage.set('guardianAuthPromoDismissed', 'true');
  };

  const handleSignOut = () => {
    // A shared station PC must not carry someone's invite into the next login.
    clearPendingJoinToken();
    // ...nor their cached conversation list (audit P-12).
    clearGuardianHistoryCache(userId);
    setConsent(null);
    safeStorage.remove('guardianAuthToken');
    safeStorage.remove('guardianUser');
    setAuthToken(null);
    setGuardianUser(null);
    setEntitlements(null);
    navigate(GUARDIAN_PATHS.chat);
  };

  const handleUserMenuSelect = (itemId) => {
    if (itemId === 'department') {
      navigate(GUARDIAN_PATHS.department);
      return;
    }
    if (itemId === 'incident-reporting') {
      navigate(GUARDIAN_PATHS.incidentReport);
      return;
    }
    // My reports (plan Track 5 B6, 2026-09-30).
    if (itemId === 'my-reports') {
      navigate(GUARDIAN_PATHS.myReports);
      return;
    }
    if (itemId === 'profile') {
      navigate(GUARDIAN_PATHS.profile);
      return;
    }
    if (itemId === 'subscriptions') {
      navigate(GUARDIAN_PATHS.subscriptions);
      return;
    }
    const frontierModel = getFrontierModel(itemId);
    if (frontierModel) {
      navigate(frontierModel.hasLiveAccess ? GUARDIAN_PATHS.chat : GUARDIAN_PATHS.model(itemId));
    }
  };

  const requestAuth = useCallback((onAuthed) => {
    if (!requiresGuardianAuth) {
      onAuthed();
      return;
    }
    openAuthModal();
  }, [requiresGuardianAuth, openAuthModal]);

  const goHome = useCallback(() => {
    navigate(GUARDIAN_PATHS.chat);
  }, [navigate]);

  return {
    authToken,
    guardianUser,
    entitlements,
    userId,
    requiresGuardianAuth,
    authError,
    handleGuardianAuthenticated,
    applyProfileFromMe,
    refreshProfile,
    consent,
    consentRequired: Boolean(authToken && consent?.required),
    handleSignOut,
    handleUnauthorized,
    requestAuth,
    authModalOpen,
    authPageMode,
    passwordResetToken,
    openAuthModal,
    closeAuthModal,
    openAuthPage,
    closeAuthPage,
    setAuthPageMode,
    setPasswordResetToken,
    activeView,
    setActiveView,
    goHome,
    promoBadgeVisible,
    dismissPromoBadge,
    handleUserMenuSelect,
  };
}
